<template>
  <div class="terms-page">
    <v-container class="py-10">
      <v-row justify="center">
        <v-col cols="12" md="8" lg="7">

          <div class="brand-header mb-8">
            <router-link to="/">
              <PassingOnLogo class="brand-logo" tone="primary" align="center" />
            </router-link>
          </div>

          <h1 class="policy-title mb-1">Terms and Conditions</h1>
          <p class="policy-updated mb-8">Last updated: March 2026</p>

          <p class="policy-intro mb-4">
            These Terms govern your use of <BrandName class="brand-inline" tone="primary" /> (the "Service"),
            operated by The Fourth Seagull Pty Ltd (ABN [insert]), registered in New South Wales, Australia.
          </p>
          <p class="policy-body mb-10">
            By using the Service, you agree to these Terms. Read them before creating an account or uploading content.
          </p>

          <section class="policy-section">
            <h2 class="section-heading">What the Service Does</h2>
            <p class="policy-body mb-4"><BrandName class="brand-inline" tone="primary" /> is an online memory platform. You can:</p>
            <ul class="policy-list mb-4">
              <li>Create private or public memories</li>
              <li>Upload photos, videos, voice recordings, stories, and comments</li>
              <li>Plan funeral details — invitations, maps, times</li>
              <li>Generate PDFs and slideshows</li>
              <li>Collect donations for memory expenses or nominated causes</li>
            </ul>
            <p class="policy-body">Use of the Service is subject to these Terms and our <router-link to="/privacy-policy" class="policy-link">Privacy Policy</router-link>.</p>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Account Creation</h2>
            <ul class="policy-list mb-2">
              <li>You must be at least 18 years old to create an account</li>
              <li>Memories are private by default. The creator controls invitations and visibility</li>
              <li>Payment is required before inviting contributors or enabling sharing features</li>
              <li>You're responsible for all activity under your account. Keep your login details secure</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Fees and Payment</h2>
            <ul class="policy-list mb-2">
              <li>Creating a memory is free</li>
              <li>To invite contributors and publish the memory, a one-time activation fee applies</li>
              <li>Each memory stays online for 12 months from activation</li>
              <li>After 12 months, the memory automatically switches to a monthly subscription. Any contributor can sponsor the subscription</li>
              <li>You can cancel the subscription at any time. If cancelled or if payment fails, you'll have a 90-day grace period before the memory and all data get deleted</li>
              <li>Stripe processes all payments. We never see or store your card details</li>
              <li>Fees are shown in Australian dollars (AUD) and include GST where applicable</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Donations</h2>
            <p class="policy-body mb-4">The donation feature lets memory creators accept contributions directly from donors toward memory costs or nominated causes.</p>
            <ul class="policy-list mb-2">
              <li>Donations are processed through Stripe Connect. Funds flow directly from the donor to the memory creator's connected Stripe account — we never hold or control donated funds</li>
              <li>We charge a 1% platform fee on top of Stripe's standard processing fees, deducted automatically</li>
              <li>Memory creators are solely responsible for how donated funds are used. We don't verify identity, intentions, or fund usage</li>
              <li>Donations to personal memories are <strong>not tax-deductible</strong>. Only donations to DGR-endorsed registered charities qualify for tax deductions</li>
              <li>Refunds aren't available once a transaction has processed, except where required by law</li>
              <li>Memory creators who accept donations must complete Stripe's onboarding process, including identity verification. Stripe's terms and conditions apply to all payment processing</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Content Ownership</h2>
            <ul class="policy-list mb-2">
              <li>You own all photos, videos, voice recordings, and text you upload</li>
              <li>Voice transcripts are generated automatically and may contain errors; the original recording remains the authoritative version</li>
              <li>By uploading content, you grant us a non-exclusive, worldwide, royalty-free licence to host, display, and reproduce it — solely to operate the Service</li>
              <li>The memory creator can delete or moderate content at their discretion</li>
              <li>Don't upload material that's unlawful, defamatory, obscene, or infringes others' rights</li>
              <li>We reserve the right to remove content or suspend access if we believe these Terms are breached or the content harms others</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Moderation</h2>
            <ul class="policy-list mb-2">
              <li>The memory creator controls moderation and visibility</li>
              <li>We may implement automated or AI-assisted moderation tools to detect and remove offensive material</li>
              <li>Users can report inappropriate content. We'll review it and may remove it</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Privacy and Data Use</h2>
            <ul class="policy-list mb-2">
              <li>We collect and use personal information in accordance with our <router-link to="/privacy-policy" class="policy-link">Privacy Policy</router-link> and the Australian Privacy Principles (Privacy Act 1988)</li>
              <li>We use your data to operate the Service, send product updates, and communicate about your account</li>
              <li>We may send occasional marketing emails about product updates. You can opt out anytime</li>
              <li>We will never sell or rent your personal data</li>
              <li>If The Fourth Seagull Pty Ltd is sold, merged, or acquired, your data may transfer to the new owner as part of that transaction</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Data Retention and Deletion</h2>
            <ul class="policy-list mb-2">
              <li>If a subscription is cancelled or payment fails after the initial 12 months, the memory enters a 90-day grace period</li>
              <li>During that period, you'll receive three automated reminders</li>
              <li>After the grace period, all data — media, comments, everything — gets permanently deleted</li>
              <li>Backup copies may be retained for up to 90 days for system integrity but won't be publicly accessible</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Intellectual Property</h2>
            <ul class="policy-list mb-2">
              <li>All software, designs, trademarks, and materials on the platform (excluding your uploaded content) belong to The Fourth Seagull Pty Ltd</li>
              <li>You can't reproduce, distribute, or use any part of the Service for commercial purposes without our written permission</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Availability and Technical Issues</h2>
            <ul class="policy-list mb-2">
              <li>We aim for reliable access but don't guarantee uninterrupted or error-free operation</li>
              <li>Scheduled maintenance or unforeseen outages may cause temporary interruptions</li>
              <li>We're not responsible for data loss caused by user error, internet issues, or third-party service failures</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Limitation of Liability</h2>
            <p class="policy-body mb-4">To the fullest extent permitted by law:</p>
            <ul class="policy-list mb-2">
              <li>The Service is provided "as is" and "as available"</li>
              <li>The Fourth Seagull Pty Ltd makes no warranties or representations about the reliability, completeness, or suitability of the Service</li>
              <li>We're not liable for any loss, damage, emotional distress, or data loss arising from use or inability to use the Service</li>
              <li>Our total liability for any claim under these Terms won't exceed the amount you paid (if any) for use of the Service during the preceding 12 months</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Termination</h2>
            <p class="policy-body mb-4">We may suspend or terminate access to your account if:</p>
            <ul class="policy-list mb-4">
              <li>You breach these Terms</li>
              <li>Your subscription is cancelled or payment fails</li>
              <li>Required by law or government authority</li>
            </ul>
            <p class="policy-body">Upon termination, your right to use the Service stops immediately. You can request a PDF export during the 90-day grace period.</p>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Changes to Terms</h2>
            <p class="policy-body">
              The Fourth Seagull Pty Ltd may update these Terms occasionally. Changes take effect when published on the website.
              Continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Governing Law</h2>
            <p class="policy-body mb-4">These Terms are governed by the laws of New South Wales, Australia.</p>
            <p class="policy-body">You agree to submit to the exclusive jurisdiction of the courts of New South Wales.</p>
          </section>

          <section class="policy-section policy-contact">
            <h2 class="section-heading">Contact</h2>
            <p class="policy-body mb-1">For questions about these Terms or the Service:</p>
            <p class="policy-body mb-1"><strong>The Fourth Seagull Pty Ltd</strong></p>
            <p class="policy-body mb-1">Email: [insert contact email]</p>
            <p class="policy-body">Registered in New South Wales, Australia</p>
          </section>

          <div class="back-link mt-10">
            <router-link to="/" class="policy-link">← Back to home</router-link>
          </div>

        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import PassingOnLogo from '../components/PassingOnLogo.vue'
import BrandName from '../components/BrandName.vue'
</script>

<style scoped lang="scss">
@use '../styles/variables' as *;

.terms-page {
  min-height: 100vh;
  background-color: $white;
}

.brand-header {
  text-align: center;
}

.brand-logo {
  height: 48px;
}

.policy-title {
  font-family: 'Alice', serif;
  font-size: 2rem;
  color: $text-primary;
}

.policy-updated {
  font-size: 0.875rem;
  color: $text-muted;
}

.policy-intro {
  font-size: 1rem;
  line-height: 1.7;
  color: $text-primary;
}

.policy-body {
  font-size: 1rem;
  line-height: 1.7;
  color: $text-secondary;
  margin: 0;
}

.policy-section {
  margin-bottom: 2.5rem;
}

.section-heading {
  font-family: 'Alice', serif;
  font-size: 1.25rem;
  color: $text-primary;
  margin-bottom: 0.75rem;
  padding-bottom: 0.4rem;
  border-bottom: $border-width solid $gray-150;
}

.subsection-heading {
  font-size: 0.95rem;
  font-weight: 600;
  color: $text-primary;
  margin-bottom: 0.5rem;
}

.policy-list {
  padding-left: 1.25rem;
  color: $text-secondary;
  line-height: 1.8;

  li {
    margin-bottom: 0.35rem;
  }
}

.policy-contact {
  background-color: $surface;
  border: $border-width solid $gray-150;
  border-radius: $border-radius;
  padding: 1.5rem;
}

.policy-link {
  color: $primary;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.back-link {
  border-top: $border-width solid $gray-150;
  padding-top: 1.5rem;
}

.brand-inline {
  font-size: 1rem;
  letter-spacing: normal;
}
</style>
